import { v4 as uuid } from 'uuid';

// Mock data is loaded dynamically to prevent bundling in production
// When process.env.MOCK_CLAIMS_TYPE is undefined (production), these dynamic imports
// will never execute and webpack will exclude the JSON files from the bundle

/**
 * Standard API status response interfaces
 */
export interface MockApiStatus {
  statusCode: number;
  code?: string;
  reason: string;
  messages?: string[];
  maxMessageLevel?: string;
  transactionId?: string;
}

export interface MockApiResponse<T = any> {
  status: MockApiStatus;
  data?: T;
}

/**
 * Helper to get mock validate session data based on claim type
 */
export async function getMockValidateSessionData(claimType: string) {
  const validateSessionResponses = await import('../../../../mock-data/validate-session-responses.json');
  const template =
    validateSessionResponses.default[claimType as keyof typeof validateSessionResponses.default] ||
    validateSessionResponses.default.default;

  // Deep clone and replace UUID placeholders
  const data = JSON.parse(JSON.stringify(template));

  // Replace UUID placeholders
  if (data.afeSessionId === '{generated-uuid}') {
    data.afeSessionId = uuid();
  }
  if (data.updatedAuthToken === '{generated-uuid}') {
    data.updatedAuthToken = 'mock-auth-token-' + uuid();
  }

  return data;
}

/**
 * Helper to get mock product-context policy data based on policy number prefix
 */
export async function getMockPolicyData(policyNumber: string) {
  const productContextPolicies = await import('../../../../mock-data/product-context-policies.json');
  const policies = productContextPolicies.default;

  let policyType: keyof typeof policies;

  if (policyNumber.startsWith('WC-')) {
    policyType = 'WorkersCompensation';
  } else if (policyNumber.startsWith('CA-')) {
    policyType = 'CommercialAuto';
  } else if (policyNumber.startsWith('CP-')) {
    policyType = 'CommercialProperty';
  } else {
    policyType = 'PersonalAuto';
  }

  console.log('🔧 Mock Mode: Returning mock product-context data for policy:', policyNumber, '(', policyType, ')');

  // Deep clone and set policy number
  const data = JSON.parse(JSON.stringify(policies[policyType]));
  data.policyNumber = policyNumber;

  return data;
}

/**
 * Check if mock mode is enabled
 */
export function isMockModeEnabled(): boolean {
  return !!(process.env.MOCK_CLAIMS_TYPE || process.env.NX_MOCK_CLAIMS_TYPE);
}

/**
 * Get the current mock claim type
 */
export function getMockClaimType(): string {
  return process.env.MOCK_CLAIMS_TYPE || process.env.NX_MOCK_CLAIMS_TYPE || 'mixed';
}

/**
 * Load mock claims data based on the environment variable
 * @returns The appropriate mock claims data based on MOCK_CLAIMS_TYPE or NX_MOCK_CLAIMS_TYPE
 */
export async function loadMockClaims() {
  const claimType = getMockClaimType();

  // Dynamically import only the needed mock data file
  switch (claimType) {
    case 'workers-comp':
      return (await import('../../../../mock-data/workers-comp-claims.json')).default;
    case 'auto':
      return (await import('../../../../mock-data/auto-claims.json')).default;
    case 'property':
      return (await import('../../../../mock-data/property-claims.json')).default;
    case 'commercial-auto':
      return (await import('../../../../mock-data/commercial-auto-claims.json')).default;
    case 'commercial-property':
      return (await import('../../../../mock-data/commercial-property-claims.json')).default;
    case 'commercial-only':
      return (await import('../../../../mock-data/commercial-only-claims.json')).default;
    case 'mixed':
    default:
      return (await import('../../../../mock-data/mixed-claims.json')).default;
  }
}

/**
 * Creates a standard success status object for mock API responses
 * @param statusCode - HTTP status code (default: 200)
 * @param includeTransactionId - Whether to include a UUID transaction ID (default: false)
 * @returns Standard status object
 */
export function createMockSuccessStatus(statusCode = 200, includeTransactionId = false): MockApiStatus {
  const status: MockApiStatus = {
    statusCode,
    code: String(statusCode),
    reason: 'OK',
    messages: [],
    maxMessageLevel: 'Info',
  };

  if (includeTransactionId) {
    status.transactionId = uuid();
  }

  return status;
}

/**
 * Creates a standard API response wrapper with success status
 * @param data - The response data
 * @param statusCode - HTTP status code (default: 200)
 * @param includeTransactionId - Whether to include a UUID transaction ID (default: false)
 * @returns Complete API response with status and data
 */
export function createMockApiResponse<T>(data: T, statusCode = 200, includeTransactionId = false): MockApiResponse<T> {
  return {
    status: createMockSuccessStatus(statusCode, includeTransactionId),
    data,
  };
}

/**
 * Creates a simplified status object (without code, messages, maxMessageLevel)
 * Used by some APIs like product-context
 * @param statusCode - HTTP status code (default: 200)
 * @param includeTransactionId - Whether to include a UUID transaction ID (default: true)
 * @returns Simplified status object
 */
export function createMockSimpleStatus(statusCode = 200, includeTransactionId = true): MockApiStatus {
  const status: MockApiStatus = {
    statusCode,
    reason: 'OK',
  };

  if (includeTransactionId) {
    status.transactionId = uuid();
  }

  return status;
}

/**
 * Gets mock claim details for a specific claim number
 * @param claimNumber - The claim number (with or without dashes)
 * @returns Mock claim details response or error response
 */
export async function getMockClaimDetails(claimNumber: string) {
  console.log('🔧 Mock Mode: Using mock claim details data');

  const mockData = await loadMockClaims();
  // Remove dashes for comparison
  const unformattedClaimNumber = claimNumber.replace(/-/g, '');

  // Find the matching claim
  const claim = mockData.claims.find((c: any) => c.claimNumber.replace(/-/g, '') === unformattedClaimNumber);

  if (!claim) {
    console.warn(`⚠️ Mock claim not found: ${claimNumber}`);
    return {
      error: 'Claim not found',
      statusCode: 404,
      message: 'Mock claim not found',
    };
  }

  console.log(`📋 Found mock claim details for: ${claimNumber}`);

  // Build contact/address info for adjuster (response[2])
  const addressInfo = [];
  if (claim.adjusterInfo) {
    const adjusterContact: any = {
      contactID: 'ADJUSTER_001',
      displayName: claim.adjusterInfo.adjusterName,
      firstName: claim.adjusterInfo.adjusterName?.split(' ')[0] || '',
      lastName: claim.adjusterInfo.adjusterName?.split(' ')[1] || '',
      emailAddresses: [
        {
          primary: true,
          emailAddress: claim.adjusterInfo.adjusterEmail,
        },
      ],
      claimRoles: [{ roleCode: 'adjuster' }],
      // Always include phoneNumbers array to match real API structure
      // When no phone exists, use empty strings (like real API)
      phoneNumbers: [
        {
          primary: true,
          areaCode: claim.adjusterInfo.adjusterPhoneNumber
            ? claim.adjusterInfo.adjusterPhoneNumber.substring(0, 3)
            : '',
          number: claim.adjusterInfo.adjusterPhoneNumber ? claim.adjusterInfo.adjusterPhoneNumber.substring(3) : '',
        },
      ],
    };

    addressInfo.push(adjusterContact);

    // Add insured contact info
    if (claim.claimantName) {
      const nameParts = claim.claimantName.split(' ');
      addressInfo.push({
        contactID: 'INSURED_001',
        displayName: claim.claimantName,
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        emailAddresses: [],
        phoneNumbers: [],
        claimRoles: [{ roleCode: 'insured' }],
      });
    }
  }

  // Build incident info for auto claims (response[1])
  // Must be an array for flatMap in mapAutoClaimDetail
  const incidentInfo = [];
  if (claim.autoClaimDetails && claim.autoClaimDetails.length > 0) {
    incidentInfo.push({
      vehicleSum: claim.autoClaimDetails.map((vehicle: any) => ({
        insuredLoss: 'insured',
        basicVehicleData: {
          Year: vehicle.year,
          Make: vehicle.make,
          Model: vehicle.model,
          VIN: vehicle.vin,
        },
      })),
    });
  }

  // Build payment info (response[3])
  const paymentInfo = claim.paymentInfo
    ? claim.paymentInfo.map((payment: any) => ({
        PayeeName: payment.PayeeName,
        Amount: parseFloat(payment.Amount.replace(/[$,]/g, '')),
        TransactionDate: payment.TransactionDate,
        CostTypeCode: 'claimcost',
        LossPartyType: 'insured',
        TransactionStatusCode: 'submitted',
        TransactionTypeCode: 'Payment',
      }))
    : [];

  // Build provider info (response[4])
  const providerInfo = claim.serviceProviderInfo || [];

  console.log('🔍 Building mock response for claim:', {
    claimNumber: claim.claimNumber,
    policyType: claim.policyType,
    hasReportedDate: !!claim.reportedDate,
    hasLossCauseDescription: !!claim.lossCauseDescription,
    hasBusinessName: !!claim.businessName,
    hasCurrentClaimProgressState: !!claim.currentClaimProgressState,
    hasLossLocationAddress: !!claim.lossLocationAddress,
    addressInfoLength: addressInfo.length,
    incidentInfoLength: incidentInfo.length,
    paymentInfoLength: paymentInfo.length,
    providerInfoLength: providerInfo.length,
  });

  const mockResponse = {
    response: [
      {
        Status: {
          code: '200',
          reason: 'OK',
        },
        claimInfo: {
          claimNumber: claim.claimNumber,
          policyNumber: claim.policyNumber,
          policyType: claim.policyType,
          reportedDate: claim.reportedDate,
          lossDate: claim.lossDate,
          openDate: claim.openDate,
          closeDate: claim.claimClosedDate,
          claimStatusCode: claim.claimStatusCode,
          claimDescription: claim.claimDescription,
          lossCauseDesc: claim.lossCauseDescription,
          lossLocationAddress: claim.lossLocationAddress,
          claimProgress: claim.currentClaimProgressState,
          businessName: claim.businessName,
          primaryFileHandler: {
            contactID: 'ADJUSTER_001',
          },
          incidents: [],
        },
      },
      incidentInfo,
      addressInfo,
      paymentInfo,
      providerInfo,
    ],
  };

  console.log('📤 Returning mock response:', {
    responseLength: mockResponse.response.length,
    hasClaimInfo: !!mockResponse.response[0].claimInfo,
    claimNumber: mockResponse.response[0].claimInfo.claimNumber,
  });

  return mockResponse;
}

/**
 * Creates a simplified API response wrapper
 * @param data - The response data
 * @param statusCode - HTTP status code (default: 200)
 * @param includeTransactionId - Whether to include a UUID transaction ID (default: true)
 * @returns Complete API response with simplified status and data
 */
export function createMockSimpleApiResponse<T>(
  data: T,
  statusCode = 200,
  includeTransactionId = true
): MockApiResponse<T> {
  return {
    status: createMockSimpleStatus(statusCode, includeTransactionId),
    data,
  };
}
